import styled from "@emotion/styled";
import {
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import {
  getBackgroundColor,
  getBorderColor,
  getHoverBackgroundColor,
  getHoverBorderColor,
} from "./selectFunctions";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const Label = styled(InputLabel, {
  shouldForwardProp: prop => prop !== "addStyle" && prop !== "isOpen",
})`
  position: absolute;
  z-index: 1;
  visibility: ${p => (p.value ? "hidden" : "visible")};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43;
  top: -9%;
  left: 7px;
  opacity: ${p => (p.value ? 1 : 0.64)};
  color: ${p => p.theme.colors.secondary.minor};

  @media (min-width: ${p => p.theme.screenSizes.mdMax}) {
    font-size: 18px;
    top: 10%;
    left: 21px;
    max-width: calc(100% - 80px);
  }

  &.Mui-focused {
    color: ${p =>
      p.isOpen
        ? p.theme.colors.secondary.title
        : p.theme.colors.secondary.minor};

    opacity: 0.64;
  }
  ${({ addStyle }) => addStyle}
`;

const SelectStyled = styled(Select, {
  shouldForwardProp: prop =>
    prop !== "isPartOfFilter" && prop !== "isOpen" && prop !== "addStyle",
})`
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 50px;
  transition: 0.3s;
  position: relative;
  cursor: pointer;
  height: 44px;
  border: 1px solid ${getBorderColor};
  background-color: ${getBackgroundColor};

  &:hover,
  &:focus-within {
    outline: none;
    border-color: ${getHoverBorderColor};
    background-color: ${getHoverBackgroundColor};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
    box-shadow: none;
    color: inherit;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  @media (min-width: ${p => p.theme.screenSizes.mdMax}) {
    height: 70px;
  }
  ${({ addStyle }) => addStyle}
`;

const BoxIcon = styled.div`
  position: absolute;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s ease;
  pointer-events: none;
  @media (min-width: ${p => p.theme.screenSizes.mdMax}) {
    right: ${p => p.theme.space[7]}px;
  }

  ${p =>
    p.isOpen &&
    `
    transform: matrix(1, 0, 0, -1, 0, 0);
    & path {
      fill: black;
    }
  `}

  ${p =>
    p.isPartOfFilter &&
    `
    & path {
      fill: black;
    }
  `}
`;

const MenuItemStyled = styled(MenuItem)`
  display: ${p => (p.hidden ? "none" : "block")};
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  color: ${p => p.theme.colors.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-height: 33px;
  padding: ${p => p.theme.space[3]}px;

  &.Mui-selected {
    background-color: ${p => p.theme.colors.accent.green};

    &:hover,
    &:focus {
      background-color: ${p => p.theme.colors.accent.green};
    }
  }

  &:hover,
  &:focus {
    background-color: ${p => p.theme.colors.background};
  }

  & + & {
    margin-top: ${p => p.theme.space[2]}px;
  }
`;

const RenderComponent = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 15px;
  margin-left: ${p => p.theme.space[3]}px;
  margin-top: ${p => p.theme.space[1]}px;
  font-weight: 500;
  color: ${p => {
    if (p.isPartOfFilter) {
      return p.value ? p.theme.colors.black : p.theme.colors.secondary.minor;
    }
    return p.value
      ? p.isOpen
        ? p.theme.colors.black
        : p.theme.colors.white
      : "inherit";
  }};
  opacity: ${p => (!p.isPartOfFilter && p.value ? 1 : "inherit")};

  @media (min-width: ${p => p.theme.screenSizes.mdMax}) {
    margin-left: 18px;
    margin-top: 2.5px;
    font-size: 18px;
    line-height: normal;
  }
`;

const ListSubheaderStyled = styled(ListSubheader)`
  position: absolute;
  z-index: 1;
  top: ${p => p.theme.space[3]}px;
  width: calc(100% - 16px);
  padding: 0;

  &.MuiInputBase-root {
    padding: 0;
  }

  &.MuiOutlinedInput-root {
    padding: 0;
  }
`;

const TextFieldStyled = styled(TextField)`
  position: relative;
  font-size: 14px;
  border-radius: 20px;
  background-color: ${p => p.theme.colors.background};
  margin-bottom: 10px;

  & .MuiInputBase-root {
    border-radius: 20px;
    padding: 0;
    font-size: 14px;
  }

  & .MuiInputBase-input {
    padding: ${p => p.theme.space[4]}px 20px;
    font-size: 14px;
  }

  & input::placeholder {
    color: ${p => p.theme.colors.secondary.gray};
  }

  & .MuiInputBase-root:hover {
    background-color: ${p => p.theme.colors.secondary.stroke};
  }

  & .MuiInputBase-root.Mui-focused {
    background-color: ${p => p.theme.colors.background};
  }

  & .MuiInputAdornment-root {
    margin-right: 0;
  }

  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: transparent;
    }

    &:hover fieldset {
      border-color: transparent;
    }

    &.Mui-focused fieldset {
      border: none transparent;
    }
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CountryName = styled.span`
  white-space: wrap;
`;

export {
  Wrapper,
  Label,
  SelectStyled,
  BoxIcon,
  MenuItemStyled,
  RenderComponent,
  ListSubheaderStyled,
  TextFieldStyled,
  IconWrapper,
  CountryName,
};
